import { createContext, FC, useContext, useEffect, useState } from 'react';
import styles from './response.module.scss';
import useThread from '@/pages/Thread/hooks/useThread';
import useItem from '@/pages/Thread/hooks/useItem';
import SourceIndicator from '../../SourceIndicator';
import CitationModal from '../CitationsModal';
import SourceDetailsModal from '../SourceDetailsModal';
import useTypingEffect from '@/pages/Thread/hooks/useTypingEffect';
import { CITATION_WRAPPER_ATTRIBUTE } from '@/pages/Thread/utils/misc';
import {
  getInlineCitations,
  PENDING_CITATION_MESSAGE,
  stripPartialMarker,
} from '@/pages/Thread/utils/inlineCitations';
import remarkInlineCitations from '@/pages/Thread/utils/remarkInlineCitations';
import { MarkdownComponent } from '@/models/markdown';
import Markdown from '../../Markdown';

interface CitationEntry {
  chunk: number;
  source: number;
}

interface ChunkContextValue {
  text: string;
  citationEntries: CitationEntry[];
  selectChunk: (chunk: number) => void;
  selectSource: (source: number, statement: string) => void;
}

/*
 * Each chunk's data reaches the Markdown components through context, so the
 * components below can be defined once. Components created during render get
 * a new identity every time, and React would rebuild the whole answer,
 * citation buttons included, on every render (losing keyboard focus).
 */
const ChunkContext = createContext<ChunkContextValue>({
  text: '',
  citationEntries: [],
  selectChunk: () => undefined,
  selectSource: () => undefined,
});

const LegacySourceIndicators: FC = () => {
  const { citationEntries, selectChunk } = useContext(ChunkContext);

  return (
    <>
      {citationEntries.map(({ chunk, source }, i) => (
        <SourceIndicator
          key={i}
          id={source}
          onClick={() => selectChunk(chunk)}
        />
      ))}
    </>
  );
};

/* Legacy citations go after the last block of the chunk they belong to */
const withSourceIndicators = (Tag: keyof JSX.IntrinsicElements) => {
  const WithSourceIndicators: FC<MarkdownComponent> = ({ children, node }) => {
    const { text, citationEntries } = useContext(ChunkContext);
    const renderCitations =
      citationEntries.length > 0 &&
      typeof node?.position?.end?.offset !== 'undefined' &&
      (Tag === 'table' ||
        !Array.isArray(children) ||
        typeof (children as Array<{ type: string }>).find(({ type }) =>
          ['ul', 'ol'].includes(type)
        ) === 'undefined') &&
      node?.position?.end?.offset === text.length;

    return (
      <Tag>
        {children}
        {renderCitations &&
          (Tag === 'table' ? (
            <tfoot>
              <tr>
                <td colSpan={100}>
                  <span className={styles['table-sources-label']}>
                    Sources:
                  </span>
                  <div>
                    <LegacySourceIndicators />
                  </div>
                </td>
              </tr>
            </tfoot>
          ) : (
            <LegacySourceIndicators />
          ))}
      </Tag>
    );
  };

  return WithSourceIndicators;
};

const TableWithSourceIndicators = withSourceIndicators('table');

const Table: FC<MarkdownComponent> = ({ children, node }) => (
  <div className={styles['table-container']}>
    <TableWithSourceIndicators node={node}>
      {children}
    </TableWithSourceIndicators>
  </div>
);

const Code: FC<MarkdownComponent> = ({ children }) => {
  if (children === '%cursor%') {
    return <i data-type="cursor"></i>;
  } else {
    return <code>{children}</code>;
  }
};

/* Rendered for each `cite` element produced by remarkInlineCitations */
const InlineCitation: FC<MarkdownComponent> = ({ node }) => {
  const { selectSource } = useContext(ChunkContext);
  const source = Number(node?.properties?.dataSource);
  const statement = String(node?.properties?.dataStatement ?? '');

  return (
    <SourceIndicator
      id={source}
      pending={node?.properties?.dataPending === true}
      inline
      onClick={() => selectSource(source, statement)}
    />
  );
};

const LEGACY_COMPONENTS: Record<string, FC<MarkdownComponent>> = {
  h1: withSourceIndicators('h4'),
  h2: withSourceIndicators('h5'),
  h3: withSourceIndicators('h6'),
  h4: withSourceIndicators('h6'),
  h5: withSourceIndicators('h6'),
  h6: withSourceIndicators('h6'),
  p: withSourceIndicators('p'),
  strong: withSourceIndicators('strong'),
  em: withSourceIndicators('em'),
  li: withSourceIndicators('li'),
  table: Table,
};

const COMPONENTS: Record<string, FC<MarkdownComponent>> = {
  table: Table,
  code: Code,
  cite: InlineCitation,
};

const Response = () => {
  const { status, processingStatus } = useThread();
  const { item, isLast } = useItem();
  const { response, sources } = item;
  const [selectedChunk, setSelectedChunk] = useState<number | null>(null);
  const [selectedSource, setSelectedSource] = useState<{
    source: number;
    statement: string;
  } | null>(null);
  const isTyping = isLast && status === 'TYPING';
  const { chunks, setChunks } = useTypingEffect(isTyping, response);
  useEffect(() => {
    if (
      status === 'READY' ||
      status === 'CITING' ||
      (status === 'FETCHING' && processingStatus)
    ) {
      setChunks(response);
    }
  }, [processingStatus, response, setChunks, status]);

  useEffect(() => {
    if (isTyping && chunks.length === 0 && response.length > 0) {
      setChunks([
        {
          text: '',
          citation: response[0].citation,
        },
      ]);
    }
  }, [chunks.length, isTyping, response, setChunks]);

  // Right after the stream ends, the typed copy is shown for one more render before the final answer replaces it
  const isStreamedText =
    isTyping || (isLast && status === 'READY' && chunks !== response);
  const sourcesCount = sources?.length ?? 0;
  const hasStreamedMarkers =
    isTyping && chunks.some(({ text }) => /\[\d+\]/.test(text));
  const inlineCitations = getInlineCitations(response, sourcesCount);
  const selectSource = (source: number, statement: string) =>
    setSelectedSource({ source, statement });

  return (
    <div
      className={styles['response']}
      data-typing={isLast && status === 'TYPING'}
    >
      {chunks.length === 0 && <p className={styles['response-part']}></p>}
      {/* The dots can't say this to screen readers one by one; say it once */}
      {isLast && (
        <p role="status" className={styles['pending-note']}>
          {hasStreamedMarkers ? PENDING_CITATION_MESSAGE : ''}
        </p>
      )}
      <div className={styles['response-part']}>
        {chunks.map((chunk, i) => {
          const { text, citation } = chunk;
          const citationEntries: CitationEntry[] = Array.isArray(citation)
            ? citation
                .map(({ source_id }) => ({
                  chunk: i,
                  source: source_id,
                }))
                .filter(
                  ({ chunk, source }) =>
                    chunk !== undefined && source !== undefined
                )
            : [];
          const isLastChunk = i === chunks.length - 1;
          const visibleText =
            isStreamedText && isLastChunk ? stripPartialMarker(text) : text;

          return (
            <ChunkContext.Provider
              key={i}
              value={{
                text,
                citationEntries,
                selectChunk: setSelectedChunk,
                selectSource,
              }}
            >
              {citationEntries.length > 0 ? (
                <div {...CITATION_WRAPPER_ATTRIBUTE}>
                  <Markdown components={LEGACY_COMPONENTS} content={text} />
                </div>
              ) : (
                <Markdown
                  components={COMPONENTS}
                  remarkPlugins={[
                    [
                      remarkInlineCitations,
                      { streaming: isStreamedText, sourcesCount },
                    ],
                  ]}
                  content={`${visibleText}${
                    status === 'TYPING' && isLastChunk ? '`%cursor%`' : ''
                  }`}
                />
              )}
            </ChunkContext.Provider>
          );
        })}
      </div>

      {selectedChunk !== null && (
        <CitationModal
          chunk={chunks[selectedChunk]}
          sources={sources}
          onClose={() => setSelectedChunk(null)}
        />
      )}

      {selectedSource !== null && sources?.[selectedSource.source - 1] && (
        <SourceDetailsModal
          source={sources[selectedSource.source - 1]}
          index={selectedSource.source}
          statement={selectedSource.statement}
          citedCount={inlineCitations.get(selectedSource.source)}
          onClose={() => setSelectedSource(null)}
        />
      )}
    </div>
  );
};

export default Response;
